import type Dexie from 'dexie';

import type {
  ConversationLastMessage,
  MessageEnvelope,
  PersistenceAdapter,
} from '@ping/ui';
import { registerWebDb } from '@ping/ui';
import { registerWebDbModule } from '@ping/ui/src/sync/syncEngine.web';

import { log } from '@core/log/log';

let installed = false;
let registeredDb: Dexie | null = null;

interface DexieMessage {
  id: string;
  conversationId: string;
  epoch: number;
  serverSeq: number;
  contentHash: string;
  senderAccountId: string;
  body?: string;
  deletedAt?: number;
  sentAt: number;
}

interface DexieConversation {
  id: string;
  kind: 'direct' | 'group';
  name?: string;
  archivedAt?: number;
  lastMessageAt?: number;
  lastMessagePreview?: string;
  lastMessageSenderAccountId?: string;
  createdAt: number;
}

function createPersistenceAdapter(db: Dexie): PersistenceAdapter {
  return {
    async writeMessage(envelope: MessageEnvelope): Promise<void> {
      const message: DexieMessage = {
        id: envelope.contentHash,
        conversationId: envelope.conversationId,
        epoch: envelope.epoch,
        serverSeq: envelope.serverSeq,
        contentHash: envelope.contentHash,
        senderAccountId: envelope.senderAccountId,
        ...(envelope.senderUserIdHex !== undefined
          ? { senderUserIdHex: envelope.senderUserIdHex }
          : {}),
        sentAt: envelope.sentAt,
      };

      if (envelope.body !== undefined) {
        message.body = envelope.body;
      }

      await db.table<DexieMessage, string>('messages').put(message);
    },

    async writeBatch(envelopes: MessageEnvelope[]): Promise<void> {
      const messages: DexieMessage[] = envelopes.map((envelope) => {
        const message: DexieMessage = {
          id: envelope.contentHash,
          conversationId: envelope.conversationId,
          epoch: envelope.epoch,
          serverSeq: envelope.serverSeq,
          contentHash: envelope.contentHash,
          senderAccountId: envelope.senderAccountId,
          ...(envelope.senderUserIdHex !== undefined
            ? { senderUserIdHex: envelope.senderUserIdHex }
            : {}),
          sentAt: envelope.sentAt,
        };

        if (envelope.body !== undefined) {
          message.body = envelope.body;
        }

        return message;
      });

      await db.table<DexieMessage, string>('messages').bulkPut(messages);
    },

    async updateConversation(
      conversationId: string,
      lastMessage: number | ConversationLastMessage,
    ): Promise<void> {
      // Legacy callers pass a bare timestamp; new callers pass the
      // full `ConversationLastMessage` object so the preview + sender
      // also propagate. Normalise both shapes into the Dexie patch.
      const patch: Partial<DexieConversation> =
        typeof lastMessage === 'number'
          ? { lastMessageAt: lastMessage }
          : {
              lastMessageAt: lastMessage.lastMessageAt,
              ...(lastMessage.preview !== undefined
                ? { lastMessagePreview: lastMessage.preview }
                : {}),
              ...(lastMessage.senderAccountId !== undefined
                ? { lastMessageSenderAccountId: lastMessage.senderAccountId }
                : {}),
            };
      await db
        .table<DexieConversation, string>('conversations')
        .update(conversationId, patch);
    },
  };
}

export function installSync(db: Dexie): PersistenceAdapter {
  if (installed) {
    log.debug({}, 'sync.install already installed');
    if (registeredDb === null) {
      throw new Error('sync.install called twice but registeredDb was null');
    }
    return createPersistenceAdapter(registeredDb);
  }

  log.info({}, 'sync.install starting');

  // biome-ignore lint/suspicious/noExplicitAny: Dexie version mismatch between apps/web and packages/ui
  registerWebDb(db as any);

  // syncEngine.web.start() pulls its persistence adapter from a separate
  // registration. Wire a thunk that returns a DbModule backed by the same
  // Dexie tables so the engine doesn't throw 'no DB module registered'
  // when App.tsx calls start(client) post-bootstrap.
  registerWebDbModule(async () => ({
    messages: {
      upsert: async (message) => {
        await db.table('messages').put(message);
      },
    },
    conversations: {
      upsert: async (conversation) => {
        await db.table('conversations').put(conversation);
      },
      get: async (id) => db.table('conversations').get(id),
    },
  }));

  registeredDb = db;
  installed = true;

  log.info({}, 'sync.install complete');

  return createPersistenceAdapter(db);
}

export function teardownSync(): void {
  if (!installed) {
    log.debug({}, 'sync.teardown already torn down');
    return;
  }

  log.info({}, 'sync.teardown starting');

  installed = false;
  registeredDb = null;

  log.info({}, 'sync.teardown complete');
}

export function isInstalled(): boolean {
  return installed;
}
